import React, {useEffect, useRef, useState} from "react";
import Flickity from 'flickity';
import 'flickity/css/flickity.css';
import Headings from "@project-stories/elements/Headings/Headings";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import { ProductTile } from "../../../ecommerce/Product/ProductTile";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import ListProductsCTA from "../ListProductsCTA/ListProductsCTA";
import CarouselNavigation from "@arctheme-components/blocks/NavigationElements/CarouselNavigation";
import useColoredTextParser from "@project-stories/../hooks/useColoredTextParser"; 
import { DosagePopup } from "../DosagePopup"
import { SecondaryCTA } from "../ListProducts";
import { SubscribePopup } from '../SubscribePopup';

export default function ListProductsDefault({ products, settings, carousel_item_type = 'carousel-item', type = "" }) {
    const { section, mobile_breakpoint, globals, product_limit } = settings;
    const isMobile = useMobileBreakpoint(mobile_breakpoint);
    const [isOpen, setIsOpen] = useState(false);
    const [dosageGuide, setDosageGuide] = useState(false);
    const [isSubscribeOpen, setIsSubscribeOpen] = useState(false);
    const [subscribeProduct, setSubscribeProduct] = useState(null);


    settings.show_price = section.product_tile_price_show;
    settings.show_quickadd = section.product_tile_quickadd_show;
    settings.show_tags = section.product_tile_tag_show;

    settings.show_indicators = section.show_indicators;
    settings.show_navbuttons = section.show_navbuttons;
    settings.use_freescroll = section.use_freescroll;
    settings.fade_edgeitems = section.fade_edgeitems;

    const renderFormattedTitle = useColoredTextParser({
		title: section?.title,
		accentTitleParts: section?.accent_title_parts,
		accentColor: section?.accent_color,
		headerStyle: isMobile ? section?.header_style_mobile : section?.header_style,
        seoStyle: section?.seo_title_font_type,
		HeadingsComponent: Headings,
	})

    const secondaryCTA = section.secondary_cta_type || SecondaryCTA.DOSAGE;

    let ctaPosition = "top";
    const carouselListRef = useRef(null);
    const flktyRef = useRef(null);
    
    // Apply product limit if set
    const limitedProducts = product_limit > 0 ? products.slice(0, product_limit) : products;

    if (isMobile) {
        ctaPosition = "bottom";
    }

        // Update navigation and indicator settings based on number of products
    if (settings.show_navbuttons) {
        settings.show_navbuttons = limitedProducts?.length > (isMobile ? 2 : 3);
    }

    if (settings.show_indicators) {
        settings.show_indicators = limitedProducts?.length > (isMobile ? 2 : 3);
    }

    useEffect(() => {
        // Initialize Flickity
        if (carouselListRef.current && !flktyRef.current) {
            flktyRef.current = new Flickity(carouselListRef.current, {
                wrapAround: false,
                pageDots:  settings.show_indicators,
                prevNextButtons:  settings.show_navbuttons,
                contain: false,
                cellAlign: 'left',
                freeScroll: settings.use_freescroll,
                percentPosition: true,
                draggable: true
            });
            
      
            window.addEventListener('resize', () => {
                if (flktyRef.current) {
                    flktyRef.current.resize();
                }
            });

        
            const images = carouselListRef.current.querySelectorAll('img');
            images.forEach(img => {
                if (img.complete) {
                    if (flktyRef.current) flktyRef.current.resize();
                } else {
                    img.addEventListener('load', () => {
                        if (flktyRef.current) flktyRef.current.resize();
                    });
                }
            });
        }

       
        return () => {
            if (flktyRef.current) {
                window.removeEventListener('resize', () => {
                    if (flktyRef.current) flktyRef.current.resize();
                });
                flktyRef.current.destroy();
            }
        };
    }, [products]);


    const onClickDosage = (productItem) => {
		const dosage = productItem?.dosage || productItem?.dosage_form;
		if( dosage ) {
			setDosageGuide(productItem)
			setIsOpen(true);
		}
	}

    // Handler to open subscribe popup
    const onClickSubscribe = (productItem) => {
        setSubscribeProduct(productItem);
        setIsSubscribeOpen(true);
    };

    if (limitedProducts.length <= 0){
        return null;
    }



    return (
        <div className="product-list default-list pl-5 lg:pl-0">
            {((section?.title || section?.subtext)) && (
                <div
                    className={`section-top max-w-[335px] lg:max-w-[384px] mx-auto text-center mb-[32px]`}
                    style={{ maxWidth: `${isMobile ? section.content_max_width_mobile : section.content_max_width_desktop}px` }}
                >
                    <div className={"section-bar w-full flex flex-row justify-between"}>
                        <div className={"section-info mx-auto"}>
                            {section?.title && renderFormattedTitle()}
                            {/* <Headings text={section?.title} type={isMobile ? section?.header_style_mobile : section?.header_style} /> */}
                            {section?.subtext && (
                                <Paragraph text={section?.subtext} type={section?.subtext_style} />
                            )}
                        </div>
                        {ctaPosition === "top" && (
                            <ListProductsCTA section={section} isMobile={isMobile} />
                        )}
                    </div>
                </div>
            )}

            <div
                className={"products-list-carousel"}
                style={{
                    width: type === 'recently-viewed' && isMobile ? `calc(100% + ${section?.padding_left_right_mobile}px)` : '100%'
                }}    
            >
                <div
                    ref={(el) => {
                          carouselListRef.current = el;
                    // If already initialized, resize the flickity instance
                    if (el &&  flktyRef.current) {
                        setTimeout(() =>  flktyRef.current.resize(), 100);
                    }
                    }}
                    className={`carousel carousel-products-list_${settings.section_id}`}
                    
                >
                    {limitedProducts && limitedProducts.length > 0 && limitedProducts.map((product, id) => {
                        const productKey =`${product.handle}-${id}-${settings.section_id}`;
                        return (
                            <div key={`${productKey}`} className={`${carousel_item_type} w-[calc(50%-15px)] lg:w-1/3 px-2 lg:px-[10px]`}>
                                <ProductTile
                                    settings={settings}
                                    product={product}
                                    type={'slider'}
                                    productKey={productKey}
                                    siblings={undefined}
                                    showQuickAdd={false}
                                    onClickDosage={() => onClickDosage(product)}
                                    onClickSubscribe={() => onClickSubscribe(product)}
                                    showDosageButton={secondaryCTA == SecondaryCTA.DOSAGE && ( product.dosage  || product.dosage_form )}
                                    showSubscribeButton={secondaryCTA == SecondaryCTA.SUBSCRIBE}
                                    noSecondaryButton={secondaryCTA == SecondaryCTA.NONE}
                                />
                            </div>
                        )
                    })}
                </div>
            </div>

            <div
                className={
                    "section-bottomrow flex flex-row justify-between items-center w-full mt-[12px]"
                }
            >
                <CarouselNavigation
                    shiftPageDots={true}
                    carouselRef={carouselListRef}
                    icons={globals.carousel_icons}
                    size={globals.carousel_icons?.carousel_navigation_arrow_size}
                />
            </div>

            <div className={"section-bottom flex justify-center mt-4"}>
                {ctaPosition === "bottom" && (
                    <ListProductsCTA section={section} isMobile={isMobile} />
                )}
            </div>
            <DosagePopup isOpen={isOpen} setIsOpen={setIsOpen} product={dosageGuide} settings={settings}/>
            {isSubscribeOpen && (
                <SubscribePopup
                    isOpen={isSubscribeOpen}
                    setIsOpen={setIsSubscribeOpen}
                    product={subscribeProduct}
                    settings={settings}
                    translations={settings.translations}
                />
            )}
        </div>
    );
}
